import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useEffect, useRef, useState } from 'react';

import { EditableSerialSearchProps } from './EditableSerialSearch.props';
import { LocationNodeData } from '../../../@types';
import { useSearchParts } from '../../../queries/live-search-query';
import { displayNodeTypeName } from '../../../utils/node-type';

export const EditableSerialSearch: React.FC<EditableSerialSearchProps> = ({
  value,
  displayValue,
  isEditable,
  ariaLabel,
  onSave,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<LocationNodeData[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selected, setSelected] = useState<LocationNodeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);

  const searchParts = useSearchParts(15);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleOpen = () => {
    setInputValue('');
    setOptions([]);
    setSelected(null);
    setOpen(true);
  };

  const handleSave = () => {
    if (!selected) {
      setOpen(false);
      return;
    }
    onSave({ serial: selected.serial1 || selected.name, partId: selected.id });
    setOpen(false);
  };

  const handleCancel = () => setOpen(false);

  const handleInputChange = (_: unknown, next: string) => {
    setInputValue(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    const term = next.trim();
    if (!term) {
      setOptions([]);
      setLoading(false);
      setSearchError(false);
      return;
    }
    setLoading(true);
    setSearchError(false);
    timerRef.current = setTimeout(async () => {
      try {
        const results = await searchParts.mutateAsync({ name: term });
        setOptions(results ?? []);
      } catch {
        setOptions([]);
        setSearchError(true);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const resolvedDisplay =
    displayValue ??
    (value || (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        N/A
      </Box>
    ));

  return (
    <Box ref={anchorRef} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box component="span" sx={{ fontSize: '13px', lineHeight: 1.3 }}>
        {resolvedDisplay}
      </Box>
      {isEditable && (
        <IconButton
          size="small"
          onClick={handleOpen}
          aria-label={`Edit ${ariaLabel}`}
          sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'v4.accent' } }}
        >
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: 1500 }}
      >
        <ClickAwayListener onClickAway={handleCancel}>
          <Paper
            elevation={8}
            sx={{
              p: 2,
              mt: 0.5,
              border: '1px solid',
              borderColor: 'v4.border',
              borderRadius: 2,
              width: 360,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Autocomplete<LocationNodeData, false, false, false>
                size="small"
                disablePortal
                options={options}
                value={selected}
                inputValue={inputValue}
                loading={loading}
                filterOptions={(opts) => opts}
                getOptionLabel={(opt) => opt?.serial1 || opt?.name || ''}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                noOptionsText={
                  searchError
                    ? 'Search failed — try again'
                    : inputValue
                      ? 'No results'
                      : 'Type to search'
                }
                onInputChange={handleInputChange}
                onChange={(_, next) => setSelected(next)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                        {option.serial1 || option.name}
                      </Typography>
                      {option.nodeTypeName && (
                        <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                          {displayNodeTypeName(option.nodeTypeName)}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    // eslint-disable-next-line jsx-a11y/no-autofocus -- opens on user intent
                    autoFocus
                    placeholder="Search by serial or part ID"
                    inputProps={{ ...params.inputProps, 'aria-label': ariaLabel }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
                  />
                )}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={handleCancel}
                  sx={{
                    fontSize: '11px',
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    color: 'text.secondary',
                    fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={handleSave}
                  disabled={!selected}
                  sx={{
                    fontSize: '11px',
                    fontWeight: 600,
                    px: 1.5,
                    py: 0.5,
                    border: 'none',
                    borderRadius: '4px',
                    bgcolor: 'v4.accent',
                    color: 'common.white',
                    cursor: selected ? 'pointer' : 'not-allowed',
                    opacity: selected ? 1 : 0.5,
                    fontFamily: 'inherit',
                    '&:hover': { opacity: selected ? 0.9 : 0.5 },
                    '&:disabled': { cursor: 'not-allowed', opacity: 0.5 },
                  }}
                >
                  Save
                </Box>
              </Box>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
